import { useSendEmailConfirmationCode } from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { useMemo } from "react"

import { useOverlayLoader } from "src/contexts"
import {
  SendEmailConfirmationCodeSuccessToast,
  SendEmailConfirmationCodeErrorToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

interface Params {
  emailAddress: string;
  language: string;
  onSuccessCallback: (emailAddress: string) => void;
}

// this hook returns a function that requests a new email confirmation code
export function useResendEmailConfirmationCode(params: Params) {
  const {
    emailAddress,
    language,
    onSuccessCallback,
  } = params

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const sendEmailConfirmationCode = useSendEmailConfirmationCode()

  // function that requests to send a new email confirmation code
  return useMemo(
    () => !sendEmailConfirmationCode
      ? undefined
      : () => {
        void (async () => {
          setIsOverlayLoaderVisible(true)

          try {
            await sendEmailConfirmationCode({
              email: emailAddress,
              lang: language,
            })

            showToast(
              "send-email-confirmation-code-success-toast",
              SendEmailConfirmationCodeSuccessToast,
            )

            onSuccessCallback(emailAddress)
          } catch (e) {
            const sugarmateError = getSugarmateError(e)

            const ErrorToast = injectProps(
              SendEmailConfirmationCodeErrorToast,
              {
                code: sugarmateError?.code,
                status: sugarmateError?.status,
              },
            )

            showToast(
              "send-email-confirmation-code-error-toast",
              ErrorToast,
            )
          } finally {
            setIsOverlayLoaderVisible(false)
          }
        })()
      },
    [
      sendEmailConfirmationCode,
      emailAddress,
      showToast,
      language,
      setIsOverlayLoaderVisible,
      onSuccessCallback,
    ],
  )
}
